/**
 * Program: Austria
 * Language: German
 * Content: Index for all German translations
 */

import { flatten } from 'flat'
import ElgibiliyImg from '@app/assets/images/faq-topic-1.png'
import TradeInImg from '@app/assets/images/faq-topic-2.png'
import ShippingImg from '@app/assets/images/faq-topic-3.png'
import PaymentImg from '@app/assets/images/faq-topic-4.png'
import FaqImg from '@app/assets/images/faq.png'
import Step1 from '@app/assets/images/step-1.jpg'
import Step2 from '@app/assets/images/step-2.jpg'
import Step3 from '@app/assets/images/step-3.jpg'
import privacy from './privacy'
import terms from './terms'
import { LangProps } from '../../types'

const de: LangProps = flatten({
  /** -------------------------------------------------------------------------- *
   * Document Titles
   * -------------------------------------------------------------------------- */
  document_title_suffix: 'Dyson Trade In mit Alchemy',
  document_title_faq: 'Häufig gestellte Fragen',
  document_title_quiz: 'Fragebogen',
  document_title_privacy: 'Datenschutzrichtlinie',
  document_title_terms: 'Geschäftsbedingungen',
  document_title_cookies: 'Cookie-Richtlinie',
  document_title_backoffice: 'Backoffice',
  document_title_signOut: 'Abmelden',
  document_title_notFound: 'Seite nicht gefunden',
  /** -------------------------------------------------------------------------- *
   * Landing Page
   * -------------------------------------------------------------------------- */
  landing_page: {
    content: {
      document_data: {
        title: 'Loslegen',
        description: '',
      },
      banner: {
        title: 'Tausche dein altes Gerät ein und spare beim Kauf eines neuen Dyson.',
        pre_title: 'Jetzt bis zu 200 € Cashback sichern¹',
        intro:
          'Mit dem Dyson Trade-In erhältst du bis zu 200 € zurück, wenn du ein teilnahmeberechtigtes Produkt kaufst und dein altes Gerät eintauschst.',
        button_text: 'Trade-In starten',
      },
      step_list: {
        title: '3 einfache Schritte zum Trade-In',
        small_print:
          '¹ Direkt bei Dyson oder bei einem teilnehmenden Einzelhandelspartner. Die Höhe des Cashbacks hängt vom gekauften Gerät ab. Siehe Teilnahmebedingungen.',
        button_text: 'Trade-In starten',
        steps: [
          {
            content:
              '### Prüfe, ob du teilnehmen kannst\n\nBeantworte unseren kurzen Fragebogen und teile uns mit, welches Produkt du eintauschen möchtest - wir sagen dir, wie viel wir dir dafür zahlen.\n\nDu musst zwischen dem 15. September und dem 31. Oktober 2025 einen Dyson V15 Detect Submarine™, Gen5detect™ Absolute, V16 Piston Animal, V16 Piston Animal Submarine™, ausgewählte Airwrap i.d.™ Modelle oder einen Airwrap Co-anda2x™ gekauft haben, um für die Inzahlungsnahme-Aktion teilnahmeberechtigt zu sein.',
            image: Step1,
          },
          {
            content:
              '### Sende uns dein altes Produkt\n\nWir stellen dir ein kostenloses vorfrankiertes Versandetikett zur Verfügung, mit dem du dein Trade-In-Gerät an uns schicken kannst.\n\nVerpacke einfach dein altes Produkt, bringe das Etikett an und gebe es bei deiner nächstgelegenen DHL-Abgabestelle ab.\n\nDu musst einen Staubsauger eintauschen, um ein Cashback für deinen neuen Dyson Staubsauger (Dyson V15 Detect Submarine™, Gen5detect™ Absolute, V16 Piston Animal, V16 Piston Animal Submarine™) zu erhalten, und ein Beauty-Gerät, um Cashback für deinen neuen Dyson Airwrap i.d.™ (ausgewählte Modelle), Airwrap Co-anda2x™ zu erhalten.',
            image: Step2,
          },
          {
            content:
              '### Auszahlung erhalten!\n\nNachdem wir dein altes Gerät in unserem Warenlager erhalten haben, veranlassen wir deine Auszahlung.\n\nDie Zahlung erfolgt 30-45 Werktage nach Erhalt und wird auf das von dir angegebene Konto überwiesen.',
            image: Step3,
          },
        ],
      },
    },
  },
  /** -------------------------------------------------------------------------- *
   * Closed Page
   * -------------------------------------------------------------------------- */
  closed_page: {
    content: {
      document_data: {
        title: 'Loslegen',
        description: '',
      },
      banner: {
        title: 'Danke für das Interesse!',
        intro: 'Leider ist die Trade In Aktion abgeschlossen',
        outro:
          'Wenn Ihre Inzahlungnahmeanfrage bereits abgeschlossen ist, stellen Sie sicher, dass Ihr alter Staubsauger 14 Tage nach Antragstellung verschickt wird.“ Anweisungen finden Sie in unserer vorherigen E-Mail.',
        image: '',
        button_text: 'Dyson Webseite',
        button_href: 'https://www.dyson.at/',
        button_type: 'absolute',
      },
    },
  },
  /** -------------------------------------------------------------------------- *
   * Quiz: Customer Page
   * -------------------------------------------------------------------------- */
  quiz_customer_page: {
    content: {
      document_data: {
        title: 'Fragebogen',
        description: '',
      },
      base: {
        title: 'Deine Angaben',
        intro:
          'Wir werden dich kontaktieren, sobald wir dein Trade-in-Gerät erhalten und deine Zahlung bearbeitet haben.',
        button_text: 'Weiter',
      },
      questions: {
        customer_name: {
          label: 'Wie lautet dein vollständiger Name?',
          placeholder: 'Vollständiger Name',
          validation_message: 'Bitte trage deinen vollen Namen ein',
        },
        customer_email: {
          label: 'Wie lautet deine E-Mail-Adresse?',
          placeholder: 'deinname@example.de',
          validation_message: 'Gebe bitte deine E-Mail-Adresse ein',
        },
      },
    },
  },
  /** -------------------------------------------------------------------------- *
   * Quiz: Purchase Page
   * -------------------------------------------------------------------------- */
  quiz_purchased_product_page: {
    content: {
      document_data: {
        title: 'Fragebogen',
        description: '',
      },
      base: {
        title: 'Kürzlichen Kauf verifizieren',
        intro:
          'Wir können nur dann ein Trade-In-Gerät annehmen, wenn du ein Teilnahmeberechtigtes Produkt bei Dyson gekauft hast.',
        button_text: 'Weiter',
      },
      questions: {
        purchased_model: {
          label: 'Welches Modell hast du gekauft?',
          validation_message: '',
        },
        serial_number_file_upload: {
          label: 'Bild der Seriennummer hochladen',
          placeholder: 'Bild hochladen • Max. 10 MB',
          validation_message: 'Bitte eine Datei hochladen',
        },
        serial_number: {
          label: 'Seriennummer',
          placeholder: '000-00-00000000',
          validation_message: 'Bitte die Seriennummer eingeben',
        },
        receipt_file_upload: {
          label: 'Bild des Kaufbelegs hochladen',
          placeholder: 'Bild hochladen • Max. 10 MB',
          validation_message: 'Bitte eine Datei hochladen',
        },
        date_of_purchase: {
          label: 'Kaufdatum',
          placeholder: 'TT / MM / JJJJ',
          validation_message: 'Bitte das Datum eingeben',
        },
        place_of_purchase: {
          label: 'Händler',
          placeholder: 'Bitte auswählen',
          validation_message: 'Bitte einen Händler auswählen',
        },
      },
      serial_number_dialog: {
        open_dialog: 'Wo finde ich sie?',
        close_dialog: 'Zurück',
        items: [
          {
            id: 1,
            content: `### Auf der Unterseite des Akkupacks\n\nDie Seriennummer ist auf der Rückseite des Akkupacks unten am Griff aufgedruckt`,
            image: '',
          },
          {
            id: 2,
            content: `### Hinter dem durchsichtigen Behälter\n\nSchau bitte auf die Unterseite des durchsichtigen Behälters gegenüber dem Auslöser, um die Seriennummer zu erkennen`,
            image: '',
          },
          {
            id: 3,
            content: `### Im Griff\n\nEntferne den einrastbaren Akku, um die Seriennummer im Griff der Maschine freizulegen`,
            image: '',
          },
        ],
      },
    },
  },
  /** -------------------------------------------------------------------------- *
   * Quiz: Trade-in Page
   * -------------------------------------------------------------------------- */
  quiz_traded_product_page: {
    content: {
      document_data: {
        title: 'Fragebogen',
        description: '',
      },
      base: {
        title: 'Dein Trade-In-Gerät',
        intro: 'Teile uns etwas über das Produkt mit, das du eintauschen möchtest, und wir nennen dir den Wert.',
        button_text: 'Weiter',
      },
      questions: {
        traded_product_manufacturer: {
          question: 'Wer ist der Hersteller?',
        },
        traded_product_model: {
          label: '',
          validation_message: '',
        },
        agree_to_terms: {
          question: 'Bestätigung der Dyson Trade-In-Bedingungen',
          checkbox_label:
            'Ich bestätige, dass mein Trade-In-Gerät die [Bedingungen des Programms erfüllt.]({terms_url}):',
          terms_summary:
            '- Das Gerät muss nicht funktionsfähig sein, muss aber mindestens das Hauptgehäuse und den Motor enthalten.\n\n- Mein Produkt ist ein reguläres, vollwertiges kabelgebundenes oder akkubetriebenes Gerät.\n\n- Ich tausche nur ein einziges teilnahmeberechtigtes Produkt ein.',
          input_error: '',
        },
      },
      offer_dialog: {
        title: 'Dein Produkt ist {value} wert',
        description:
          'Wir überweisen dir {value}, sobald wir dein Trade-In-Gerät erhalten und unsere Techniker bestätigt haben, dass es deiner Beschreibung entspricht.\n\nSollten Abweichungen zwischen deiner Beschreibung und dem eintreffenden Produkt bestehen, kann sich die Bewertung ändern.',
        button_text: 'Weiter',
      },
    },
  },
  /** -------------------------------------------------------------------------- *
   * Quiz: Shipping Page
   * -------------------------------------------------------------------------- */
  quiz_address_page: {
    content: {
      document_data: {
        title: 'Fragebogen',
        description: '',
      },
      base: {
        title: 'Kontaktadresse',
        intro: 'Wir benötigen deine Adresse, um dir ein kostenloses Versandetikett zu erstellen.',
        button_text: 'Weiter',
      },
      questions: {
        lookup_address: {
          question: 'Adresse suchen',
          placeholder: 'Adresse eingeben',
          manual_address_prompt: 'Siehst du deine Adresse nicht? Bitte manuell hinzufügen.',
        },
        manual_enter_address: {
          street_address_label: 'Straßenadresse',
          city_label: 'Stadt',
          postal_code_label: 'Postleitzahl',
          country_label: 'Land',
        },
      },
    },
  },
  /** -------------------------------------------------------------------------- *
   * Quiz: Payment Page
   * -------------------------------------------------------------------------- */
  quiz_payment_page: {
    content: {
      document_data: {
        title: 'Fragebogen',
        description: '',
      },
      base: {
        title: 'Zahlungsangaben',
        intro:
          'Bitte gebe die Bankverbindung der Person an, an die wir die Zahlung leisten sollen. Bitte überprüfe diese Angaben sorgfältig, da wir für Fehler keine Haftung übernehmen.',
        button_text: 'Weiter',
      },
      questions: {
        account_holder: {
          label: 'Kontoinhaber',
          placeholder: 'Name des Kontoinhabers',
          validation_message: 'Bitte den Namen des Kontoinhabers eingeben',
        },
        iban: {
          label: 'IBAN',
          placeholder: 'e.g. AT00 0000 0000 0000 0000',
          validation_message: 'Bitte dein IBAN eingeben',
        },
        agree_to_terms: {
          checkbox_label:
            'Ich habe die [Allgemeinen Geschäftsbedingungen]({terms_url}) und [die Datenschutzrichtlinie]({privacy_url}) gelesen und akzeptiere sie.',
        },
      },
      confirmation_dialog: {
        title: 'Zahlungsangaben bestätigen',
        intro: 'Bitte stelle sicher, dass die von dir eingegebenen Zahlungsangaben korrekt sind.',
        cancel_button: 'Zurück',
        confirm_button: 'Bestätigen',
      },
    },
  },
  /** -------------------------------------------------------------------------- *
   * Order Confirmation Page
   * -------------------------------------------------------------------------- */
  order_confirmation_page: {
    content: {
      document_data: {
        title: 'Fragebogen',
        description: '',
      },
      banner: {
        title: 'Danke, dein trade-in wurde erstellt!',
      },
      step_list: {
        title: 'Was sind die nächsten Schritte?',
        steps: [
          {
            content:
              '#### Bitte stelle sicher, dass die von dir eingegebenen Zahlungsangaben korrekt sind\n\nWir müssen noch einige abschließende Überprüfungen der von dir gemachten Angaben durchführen - aber keine Sorge, das ist normal und dauert in der Regel nicht länger als 1 Werktag.\n\nSollten wir weitere Informationen benötigen, um die Prüfung abzuschließen, kontaktieren wir dich per E-Mail.',
          },
          {
            content:
              '#### Verpacke und versende dein Trade-In-Produkt\n\nWir senden dir ein kostenloses Versandetikett zu.\n\nSobald du es erhalten hast, verpacke bitte dein Trade-In-Gerät in einem geeigteten Karton und bringe das kostenlose vorfrankierte Versandetikett an.',
          },
          {
            content:
              '#### Auszahlung erhalten!\n\nWir prüfen Ihr Produkt nach Erhalt und überweisen Ihnen bei Übereinstimmung mit der Beschreibung den Betrag auf das von Ihnen angegebene Konto:',
            footnote: 'Die Zahlung erfolgt 30-45 Werktage, nachdem wir dein Produkt erhalten haben.',
          },
        ],
      },
    },
  },
  /** -------------------------------------------------------------------------- *
   * Partial: FAQ Section
   * -------------------------------------------------------------------------- */
  faq_banner: {
    content: {
      title: 'Hast du Fragen?',
      image: FaqImg,
      button_text: 'Unsere FAQs',
    },
  },
  /** -------------------------------------------------------------------------- *
   * Partial: Quiz Tracker
   * -------------------------------------------------------------------------- */
  quiz_tracker: {
    content: {
      purchase_step: {
        long_name: 'Kürzlichen Kauf verifizieren',
        short_name: 'Kürzlichen Kauf verifizieren',
      },
      valuation_step: {
        long_name: 'Dein alter Staubsauger',
        short_name: 'Trade-In Bewertung',
      },
      address_step: {
        long_name: 'Kontaktadresse',
        short_name: 'Kontaktadresse',
      },
      payment_step: {
        long_name: 'Zahlungsangaben',
        short_name: 'Zahlungsangaben',
      },
    },
  },
  /** -------------------------------------------------------------------------- *
   * FAQ Page
   * -------------------------------------------------------------------------- */
  faq_page: {
    content: {
      document_data: {
        title: '',
        description: '',
      },
      search: {
        title: 'FAQs',
        placeholder_text: 'Tippe um zu suchen',
      },
      topic_list: {
        title: 'Themen durchsuchen',
        topics: [
          {
            id: 1,
            title: 'Beantragung der Inzahlungnahme',
            icon: ElgibiliyImg,
            questions: [
              {
                question: 'Wo kann ich meinen Dyson Trade-In-Antrag ausfüllen?',
                answer: 'Besuche die Dyson Webseite, um deinen Trade-In-Antrag auszufüllen.',
              },
              {
                question: 'Welche Produkte sind qualifiziert?',
                answer:
                  'Um sich für die Inzahlungnahme zu qualifizieren, musst du eines dieser Produkte kaufen: V15s Detect Submarine™, Gen5detect™ Absolute, V16 Piston Animal, V16 Piston Animal Submarine™, Airwrap Co-anda2x™, Airwrap i.d.™\n\nDeine Inzahlungnahme-Rückerstattung variiert je nach gekauftem Produkt:\n- Gen5detect™ Absolute, V15s Detect Submarine™: 200 Euro für jedes in Zahlung gegebene Gerät (Dyson oder andere Marken)\n- V16 Piston Animal, V16 Piston Animal Submarine™ : 150 Euro für jedes in Zahlung gegebene Gerät (Dyson oder andere Marken)\n- Dyson Airwrap i.d.™ (ausgewählte Modelle), Airwrap Co-anda2x™: 100 Euro für jedes in Zahlung gegebene Dyson-Beauty-Gerät, 75 Euro für Beauty Geräte einer anderen Marke\n\nDu kannst nur alte Staubsauger in Zahlung geben, um ein Cashback für einen neuen Dyson Staubsauger zu erhalten und alte Beauty-Geräte nur für ein neues Dyson Beauty-Gerät.',
              },
              {
                question: 'Wann läuft die Trade-In-Aktion?',
                answer:
                  'Du mußt zwischen dem 15. September und dem 31. Oktober (jeweils einschließlich) ein qualifiziertes Produkt kaufen.\n\nAnsprüche müssen innerhalb von 14 Tagen ab Kaufdatum über das Trade-In-Portal geltend gemacht werden.\n\nAlle eingetauschten Produkte müssen innerhalb von 14 Tagen nach Antragstellung an uns eingesandt werden.',
              },
              {
                question: 'Welche Händler nehmen an der Aktion teil?',
                answer:
                  'Du mußt dein neues Dyson-Produkt über einen der folgenden Wege gekauft haben, um für das Inzahlungnahmeprogramm berechtigt zu sein:\n1. Von der Dyson-Website\n2. Aus einem Dyson Store (Österreich: SCS (Vösendorf), Steffl (Wien), PlusCity (Pasching), Designer Outlet (Parndorf). Deutschland: Mönckebergstraße (Hamburg), Westfield Centro (Oberhausen), Tauentzienstraße (Berlin), Hohe Straße (Köln), MyZeil (Frankfurt am Main), Olympia (Einkaufszentrum))\n3. Alle indirekten Dyson-Einzelhandelspartner aus dieser Liste: Österreich (Electronnic Partner, Expert, Red Zac, Metro, E4you, Mediamarkt & Saturn, Baslerbeauty, Kastner & Öhler, 0815, Otto), Deutschland (Mediamarkt & Saturn, Breuninger, Expert, Euronics, Metro, QVC, KaDeWe, Otto, Flaconi)',
              },
              {
                question: 'Was sind die Bedingungen der Trade-In-Aktion?',
                answer:
                  'Wenn du ein qualifiziertes Dyson-Produkt kaufst und ein altes Produkt in Zahlung gibst, erhältst du eine Barauszahlung auf dein angegebenes Bankkonto.\n\nDie vollständigen Geschäftsbedingungen findest du in der Fußzeile dieser Webseite.',
              },
              {
                question: 'Wo muss ich wohnen, um an diesem Programm teilzunehmen?',
                answer:
                  'Um an der Inzahlungnahme-Aktion teilnehmen zu können, muß das Gerät, das du eintauschen möchtest, an einer der dafür vorgesehenen DHL-Abholstellen in Deutschland oder Österreich abgeben werden können und du über ein Bankkonto in Deutschland oder Österreich (je nachdem, aus welchem Land du dich bewirbst) oder einem anderen EWR-Land verfügen.',
              },
              {
                question: 'Welche Angaben benötige ich zum Ausfüllen des Formulars?',
                answer:
                  'Du benötigst den Namen und die Seriennummer deines neuen Dyson-Produkts, dein Kaufbeleg, die Einzelheiten zu dem Produkt, das du eintauschen willst, und deine Daten, einschließlich deiner Bankkontonummer, um die Zahlung zu erhalten.',
              },
              {
                question: 'Was passiert, wenn ich den Kaufbeleg für meinen neuen Dyson verloren habe?',
                answer:
                  'Du mußt bei deinem Händler einen neuen Zahlungsbeleg anfordern. Ohne den Beleg können wir deinen trade-in-Antrag nicht bearbeiten.\n\nWenn der Beleg nicht verfügbar ist, sende uns bitte eine elektronische Kaufbestätigung, aus der das Datum, der Name des gekauften Produkts und der Händler ersichtlich sind.',
              },
              {
                question: 'Wer leitet die Dyson Trade-In-Aktion?',
                answer:
                  'Die Trade-In-Aktion wird im Auftrag von Dyson von [Alchemy](https://www.wearealchemy.com/) durchgeführt, einem weltweit führenden Unternehmen im Bereich Kreislaufwirtschaft und Trade-In-Marktplatz.',
              },
              {
                question: 'Was passiert, nachdem ich meinen Trade-In-Antrag gestellt habe?',
                answer:
                  'Du erhältst eine E-Mail, die den Eingang deines Antrags bestätigt, und dich über deine Trade-In-Referenznummer informiert.',
              },
              {
                question: 'Wie lange dauert es, bis mein Trade-In-Antrag genehmigt wird?',
                answer:
                  'Dein Antrag wird geprüft und innerhalb eines Werktags (Mo.-Fr.) wird eine Entscheidung über die Genehmigung getroffen.\n\nNach Genehmigung deines Antrags erhältst du eine E-Mail mit der Genehmigung und einem vorausbezahlten Versandetikett, das du ausdrucken und auf die Verpackung des einzutauschenden Produkts geklebt werden muß.',
              },
              {
                question:
                  'Ich habe keine E-Mail erhalten oder mein Antrag wurde nicht genehmigt. An wen kann ich mich wenden?',
                answer:
                  'Du kannst das Kundenserviceteam von Alchemy unter [dyson@wearealchemy.com](mailto:dyson@wearealchemy.com) oder über die Chat-Funktion im Trade-In-Link auf der Dyson Website kontaktieren.',
              },
            ],
          },
          {
            id: 2,
            title: 'Verpackung und Versand deines alten Produkts',
            icon: TradeInImg,
            questions: [
              {
                question: 'Wie verpacke ich mein altes Produkt?',
                answer:
                  'Du benötigst für den Versand deines Produkts einen geeigneten Karton. Das Produkt sollte sicher verpackt sein und nicht zu viel Platz im Karton lassen. Bitte verwende einen Karton mit der richtigen Größe.\n\nBefestige das vorausbezahlte Versandetikett sicher an diesem Karton.',
              },
              {
                question: 'Kann ich jede Art von Karton verwenden?',
                answer:
                  'Ja, du kannst jeden geeigneten Karton verwenden. Du kannst den Originalkarton des neuen Produkts verwenden, falls du diesen noch hast, oder einen anderen geeigneten Karton.',
              },
              {
                question: 'Was muss ich in der Box einsenden?',
                answer:
                  'Wenn du einen Staubsauger eintauscht, muss dieser intakt und vollständig sein ( nicht beschädigt oder zerbrochen). Es muss sich um einen normalen Staubsauger in Standardgröße mit Kabel oder Akku handeln. Achte darauf, dass der Staubbehälter oder -beutel leer ist. Das Gerät musst nicht funktionsfähig sein. Du musst keine Saugstäbe, Schläuche, Kabel, Akkus oder sonstiges Zubehör einsenden (diese sind nicht erforderlich, nur das Hauptgerät).\n\nWenn du ein Beauty-Gerät eintauscht, muss es intakt und vollständig sein (nicht beschädigt oder zerbrochen). Es muss sich um ein Standardprodukt in Originalgröße handeln. Die Inzahlungnahme ist mit jedem Haargerät möglich – zum Beispiel einem Haartrockner, einem Multistyler oder einem Lockenstab – unabhängig von Marke und Preis. Du mußt weder Kabel noch anderes Zubehör einsenden (diese werden nicht benötigt, nur das Hauptgerät).\n\nDu kannst nur ein gültiges Produkt eintauschen.',
              },
              {
                question: 'Versand von Batterien',
                answer:
                  'Bitte beachte: Wenn das Gerät eine Batterie hat und du diese miteinsenden möchtest, muss diese intakt und frei von physischen Schäden sein.\n\nSende bitte keine beschädigten Batterien im Paket.\n\nVerwende keinen Gefahrgutaufkleber – für den Versand einer einzelnen Batterie ist kein Gefahrgutaufkleber erforderlich.',
              },
              {
                question: 'Wie bereite ich das Paket für den Versand vor?',
                answer:
                  'Stelle sicher, dass die Box versiegelt und sicher ist. Bitte verwende den kleinstmöglichen Behälter, ohne unnötigen Platz einzunehmen.“\n\nDrucke das von uns per E-Mail bereitgestellte vorfrankierte Versandetikett aus und befestige es sicher am Paket.\n\nAuf deinem Karton sollte KEIN Gefahrgutetikett angebracht sein. Wenn dies der Fall ist, klebe bitte das vorfrankierte Versandetikett oben darauf, so dass kein Gefahrgutetikett sichtbar ist. Wenn dieser Hinweis sichtbar ist, kann ein Bußgeld verhängt werden.\n\nGebe das Paket bei einem DHL-Abgabezentrum ab.',
              },
            ],
          },
          {
            id: 3,
            title: 'Nachdem ich mein Produkt eingetauscht habe',
            icon: ShippingImg,
            questions: [
              {
                question: 'Wie erfahre ich, wann mein eingetauschtes Produkt eingegangen ist?',
                answer:
                  'Wir senden dir eine E-Mail, wenn wir dein Produkt erhalten haben, bestätigen, dass wir es empfangen haben und dass der geforderte Betrag an die Daten überwiesen wird, die du in deinem Inzahlungnahme-Antrag angegeben hast.',
              },
              {
                question: 'Wie lange dauert es, bis der Eingang bestätigt wird?',
                answer:
                  'Normalerweise bestätigen wir dir den Eingang des eingetauschten Produkts in unserem Lager innerhalb von 7 Tagen nach dem Versand per E-Mail an dich.',
              },
              {
                question: 'Was passiert im Falle einer Beanstandung des Trade-In-Produkts?',
                answer:
                  'Für den Fall, dass was du uns übermittelt hast, nicht mit deinen Angaben in deinem Antrag übereinstimmt, wird sich unser Kundenservice umgehend mit dir in Verbindung setzen.',
              },
              {
                question: 'Wie lange wird es dauern, bis ich meine Auszahlung erhalte?',
                answer:
                  'Nach der Überprüfung deines neuen qualifizierten Dyson-Kaufs und dem Erhalt deines Inzahlungnahmeprodukts überweisen wir die Zahlung innerhalb von 30 bis 45 Tagen nach Erhalt deines Produkts auf dein angegebenes Konto.',
              },
            ],
          },
          {
            id: 4,
            title: 'Allgemein',
            icon: PaymentImg,
            questions: [
              {
                question: 'Wie kann ich mit dem Kundendienst der Trade-In-Aktion in Kontakt treten?',
                answer:
                  'Wenn du Fragen zur Trade-In-Aktion oder zu deiner Bewerbung hast, kannst du das Alchemy-Team unter [dyson@wearealchemy.com](dyson@wearealchemy.com) erreichen.',
              },
              {
                question: 'Öffnungszeiten des Kundendienstes',
                answer:
                  'Wir sind werktags ( Mo. - Fr. ) von 10:00 bis 16:00 Uhr erreichbar und bemühen uns, alle Anfragen innerhalb von maximal einem Arbeitstag zu beantworten.',
              },
            ],
          },
        ],
      },
    },
  },
  /** -------------------------------------------------------------------------- *
   * Terms and Conditions Page
   * -------------------------------------------------------------------------- */
  terms_and_conditions_page: {
    content: {
      document_data: {
        title: '',
        description: '',
      },
      page_settings: {
        title: 'Allgemeine Geschäftsbedingungen für den Dyson Trade In Service',
        body: terms,
      },
    },
  },
  /** -------------------------------------------------------------------------- *
   * Privacy Policy Page
   * -------------------------------------------------------------------------- */
  privacy_policy_page: {
    content: {
      document_data: {
        title: '',
        description: '',
      },
      page_settings: {
        title: 'Datenschutzrichtlinie von Alchemy (Großbritannien und Europa)',
        body: privacy,
      },
    },
  },
})

export default de
